import { CancelOrderReason } from '@/services/innovorder/orders/order.enum';
import { ManualPaymentMethodCode } from '@/services/innovorder/orders/order.type';
import { useState, useMemo, useCallback, ChangeEvent } from 'react';
import { Option } from '@/components/atoms/Select';
import { useGetManualPaymentMethodCodeOptionsQuery } from '@/services/innovorder/orders/order.endpoints';
import { PaymentMethodCode } from '@/services/innovorder/paymentMethod/paymentMethod.type';
import { OrderDetailsCancelConfirmationProps } from './OrderDetailsCancelConfirmation';

type OrderDetailsCancelConfirmationViewModel = {
    CancelOrderReasons: Option<CancelOrderReason | undefined>[];
    cancelReason: CancelOrderReason | undefined;
    handleChangeReason: (reason?: CancelOrderReason) => void;
    manualPaymentMethodCode: ManualPaymentMethodCode | undefined;
    handleChangeManualPaymentMethodCode: (paymentMethodCode?: ManualPaymentMethodCode) => void;
    isDisabled: boolean;
    handleValidate: () => void;
    step: 'reason' | 'manualPaymentMethod';
    ManualPaymentMethodCodes: Option<ManualPaymentMethodCode>[] | undefined;
    cancellationReasonDescription: string | undefined;
    handleChangeCancellationReasonDescription: (event: ChangeEvent<HTMLTextAreaElement>) => void;
    isLoadingManualPaymentMethodCodeList: boolean;
};

export function useOrderDetailsCancelConfirmationVM({
    hasManualPaymentMethodToChoose,
    defaultManualPaymentMethodCode,
    onValidate,
    channelId,
    restaurantId,
    consumptionModeId,
}: OrderDetailsCancelConfirmationProps): OrderDetailsCancelConfirmationViewModel {
    const [step, setStep] = useState<'reason' | 'manualPaymentMethod'>('reason');
    const [cancelReason, setCancelReason] = useState<CancelOrderReason>();
    const [cancellationReasonDescription, setCancellationReasonDescription] = useState<string>();
    const [manualPaymentMethodCode, setManualPaymentMethodCode] = useState<ManualPaymentMethodCode>(undefined);

    const { data: dataManualPaymentMethodCodes, isLoading: isLoadingManualPaymentMethodCodeList } =
        useGetManualPaymentMethodCodeOptionsQuery(
            {
                channelId,
                restaurantId,
                consumptionModeId,
            },
            { skip: step === 'reason' },
        );

    const isDisabled = useMemo(() => {
        if (isLoadingManualPaymentMethodCodeList) {
            return true;
        }
        if (step === 'reason') {
            return !cancelReason;
        }
        return !manualPaymentMethodCode;
    }, [step, cancelReason, manualPaymentMethodCode, isLoadingManualPaymentMethodCodeList]);

    const CancelOrderReasons: Option<CancelOrderReason | undefined>[] = useMemo(
        () => [
            {
                label: `orderDetail.cancel.reason.${CancelOrderReason.CannotFulfillOrder}`,
                value: CancelOrderReason.CannotFulfillOrder,
            },
            {
                label: `orderDetail.cancel.reason.${CancelOrderReason.OrderingMistake}`,
                value: CancelOrderReason.OrderingMistake,
            },
            {
                label: `orderDetail.cancel.reason.${CancelOrderReason.UnsatisfiedCustomer}`,
                value: CancelOrderReason.UnsatisfiedCustomer,
            },
            {
                label: `orderDetail.cancel.reason.${CancelOrderReason.CustomerNoShow}`,
                value: CancelOrderReason.CustomerNoShow,
            },
            {
                label: `orderDetail.cancel.reason.${CancelOrderReason.CustomerLeaves}`,
                value: CancelOrderReason.CustomerLeaves,
            },
            {
                label: `orderDetail.cancel.reason.${CancelOrderReason.LearningMode}`,
                value: CancelOrderReason.LearningMode,
            },
            { label: `orderDetail.cancel.reason.${CancelOrderReason.Technical}`, value: CancelOrderReason.Technical },
            { label: `orderDetail.cancel.reason.${CancelOrderReason.Other}`, value: CancelOrderReason.Other },
        ],
        [],
    );
    const handleChangeReason = useCallback((reason?: CancelOrderReason) => {
        setCancelReason(reason);
    }, []);
    const ManualPaymentMethodCodes: Option<ManualPaymentMethodCode>[] = useMemo(() => {
        if (!dataManualPaymentMethodCodes || dataManualPaymentMethodCodes.length === 0) {
            return [
                {
                    label: `orderDetail.cancel.manualPaymentMethod.${PaymentMethodCode.Cash}`,
                    value: PaymentMethodCode.Cash,
                },
                {
                    label: `orderDetail.cancel.manualPaymentMethod.${PaymentMethodCode.CreditCard}`,
                    value: PaymentMethodCode.CreditCard,
                },
            ];
        }
        return dataManualPaymentMethodCodes.map((code) => ({ label: code.label, value: code.value }));
    }, [dataManualPaymentMethodCodes]);
    const handleChangeManualPaymentMethodCode = useCallback((paymentMethodCode?: ManualPaymentMethodCode) => {
        setManualPaymentMethodCode(paymentMethodCode);
    }, []);

    const handleChangeCancellationReasonDescription = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setCancellationReasonDescription(event.target.value);
    };

    const handleValidateWithoutManualPaymentMethodCode = useCallback(() => {
        if (cancelReason) {
            onValidate(cancelReason, cancellationReasonDescription);
        }
    }, [cancelReason, onValidate, cancellationReasonDescription]);

    const handleValidateWithManualPaymentMethodCode = useCallback(() => {
        if (!manualPaymentMethodCode) {
            setStep('manualPaymentMethod');
            setManualPaymentMethodCode(defaultManualPaymentMethodCode);
            return;
        }
        if (cancelReason && manualPaymentMethodCode) {
            onValidate(cancelReason, cancellationReasonDescription, manualPaymentMethodCode);
        }
    }, [
        cancelReason,
        onValidate,
        manualPaymentMethodCode,
        cancellationReasonDescription,
        defaultManualPaymentMethodCode,
    ]);

    const handleValidate = useCallback(() => {
        if (hasManualPaymentMethodToChoose) {
            handleValidateWithManualPaymentMethodCode();
        } else {
            handleValidateWithoutManualPaymentMethodCode();
        }
    }, [
        hasManualPaymentMethodToChoose,
        handleValidateWithManualPaymentMethodCode,
        handleValidateWithoutManualPaymentMethodCode,
    ]);

    return {
        CancelOrderReasons,
        cancelReason,
        handleChangeReason,
        manualPaymentMethodCode,
        handleChangeManualPaymentMethodCode,
        isDisabled,
        handleValidate,
        step,
        ManualPaymentMethodCodes,
        cancellationReasonDescription,
        handleChangeCancellationReasonDescription,
        isLoadingManualPaymentMethodCodeList,
    };
}
